export type ReasonEntry = {
  text: string
  lastUsedAt: number
  usageCount: number
}

export interface ReasonStorage {
  list(scope: string): Promise<ReasonEntry[]>
  commit(scope: string, text: string): Promise<void>
  remove(scope: string, text: string): Promise<void>
}

const LS_PREFIX = 'crm.reasonHistory.'
const MAX_ENTRIES_PER_SCOPE = 50

export class LocalStorageReasonStorage implements ReasonStorage {
  private key(scope: string) {
    return LS_PREFIX + scope
  }

  private read(scope: string): ReasonEntry[] {
    try {
      const raw = localStorage.getItem(this.key(scope))
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  private write(scope: string, entries: ReasonEntry[]) {
    try {
      localStorage.setItem(this.key(scope), JSON.stringify(entries))
    } catch {
      // storage full or blocked — silently skip
    }
  }

  async list(scope: string): Promise<ReasonEntry[]> {
    return this.read(scope).sort((a, b) => {
      if (b.usageCount !== a.usageCount) return b.usageCount - a.usageCount
      return b.lastUsedAt - a.lastUsedAt
    })
  }

  async commit(scope: string, text: string): Promise<void> {
    const clean = text.trim()
    if (!clean) return
    const entries = this.read(scope)
    const existing = entries.find((e) => e.text === clean)
    if (existing) {
      existing.usageCount += 1
      existing.lastUsedAt = Date.now()
    } else {
      entries.push({ text: clean, usageCount: 1, lastUsedAt: Date.now() })
    }
    entries.sort((a, b) => b.lastUsedAt - a.lastUsedAt)
    this.write(scope, entries.slice(0, MAX_ENTRIES_PER_SCOPE))
  }

  async remove(scope: string, text: string): Promise<void> {
    const entries = this.read(scope).filter((e) => e.text !== text)
    this.write(scope, entries)
  }
}
